<template>
  <div class="container">
    <div class="container01">
      <h1 class="container01_h1">数据统计</h1>
      
      <!-- 添加返回按钮 -->
      <div class="header-actions">
        <el-button @click="$router.back()" class="back-button">
          <i class="el-icon-arrow-left"></i> 返回上一级
        </el-button>
      </div>
      
      <el-tabs v-model="activeTab" type="card" class="stats-tabs">
        <!-- 每日采购汇总 -->
        <el-tab-pane label="每日采购汇总" name="daily">
          <el-card class="stats-card">
            <el-form :model="dailyForm" label-width="100px" class="filter-form">
              <el-form-item label="统计日期">
                <el-date-picker
                  v-model="dailyForm.date"
                  type="date"
                  placeholder="选择统计日期"
                  value-format="YYYY-MM-DD"
                  @change="loadDailySummary">
                </el-date-picker>
              </el-form-item>
            </el-form>
            
            <!-- 添加表格容器，设置固定高度 -->
            <div class="table-container">
              <el-table 
                :data="dailyData.categories" 
                v-loading="dailyLoading" 
                border
                style="width: 100%"
                :height="tableHeight">
                <el-table-column type="expand">
                  <template #default="props">
                    <div class="nested-table-container">
                      <el-table :data="props.row.merchandises" border>
                        <el-table-column prop="name" label="商品名称" width="200"></el-table-column>
                        <el-table-column prop="specification" label="规格" width="150"></el-table-column>
                        <el-table-column prop="total_quantity" label="总数量" width="100" align="right">
                          <template #default="scope">
                            {{ scope.row.total_quantity }}
                          </template>
                        </el-table-column>
                        <el-table-column prop="customer_demands" label="客户需求来源"></el-table-column>
                      </el-table>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="category" label="商品分类"></el-table-column>
                <el-table-column label="商品数量" width="120" align="right">
                  <template #default="props">
                    {{ props.row.merchandises?.length || 0 }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <div class="summary-info">
              <p>统计日期: {{ dailyData.date }} | 分类数量: {{ dailyData.total_categories }} | 商品种类: {{ dailyData.total_merchandises }}</p>
            </div>
          </el-card>
        </el-tab-pane>
        
        <!-- 月度客户汇总 -->
        <el-tab-pane label="月度客户汇总" name="monthly">
          <el-card class="stats-card">
            <el-form :model="monthlyForm" label-width="100px" class="filter-form">
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="年份">
                    <el-input-number
                      v-model="monthlyForm.year"
                      :min="2020"
                      :max="2030"
                      @change="loadMonthlySummary">
                    </el-input-number>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="月份">
                    <el-input-number
                      v-model="monthlyForm.month"
                      :min="1"
                      :max="12"
                      @change="loadMonthlySummary">
                    </el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
            
            <!-- 添加表格容器，设置固定高度 -->
            <div class="table-container">
              <el-table 
                :data="monthlyData.customers" 
                v-loading="monthlyLoading" 
                border
                style="width: 100%"
                :height="tableHeight">
                <el-table-column type="expand">
                  <template #default="props">
                    <div class="nested-table-container">
                      <el-table :data="props.row.orders" border>
                        <el-table-column prop="order_number" label="订单编号" width="150"></el-table-column>
                        <el-table-column prop="order_time" label="下单时间" width="180">
                          <template #default="scope">
                            {{ formatDateTime(scope.row.order_time) }}
                          </template>
                        </el-table-column>
                        <el-table-column prop="amount" label="订单金额" width="120" align="right">
                          <template #default="scope">
                            ¥{{ scope.row.amount?.toFixed(2) || '0.00' }}
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="name" label="客户名称"></el-table-column>
                <el-table-column prop="total_amount" label="总金额" width="150" align="right">
                  <template #default="scope">
                    <span class="amount-highlight">
                      ¥{{ scope.row.total_amount?.toFixed(2) || '0.00' }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="订单数量" width="120" align="right">
                  <template #default="props">
                    {{ props.row.orders?.length || 0 }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <div class="summary-info">
              <p>
                {{ monthlyData.year }}年{{ monthlyData.month }}月 | 
                客户数量: {{ monthlyData.total_customers }} | 
                总金额: ¥{{ monthlyData.total_amount?.toFixed(2) || '0.00' }}
              </p>
            </div>
          </el-card>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import statisticsAPI from '@/api/statistics'

export default {
  name: 'StatisticsView',
  data() {
    const today = new Date()
    return {
      activeTab: 'daily',
      dailyLoading: false,
      monthlyLoading: false,
      dailyForm: {
        date: today.toISOString().split('T')[0]
      },
      monthlyForm: {
        year: today.getFullYear(),
        month: today.getMonth() + 1
      },
      dailyData: {
        date: '',
        total_categories: 0,
        total_merchandises: 0,
        categories: []
      },
      monthlyData: {
        year: 0,
        month: 0,
        total_customers: 0,
        total_amount: 0,
        customers: []
      },
      tableHeight: '500px' // 固定表格高度
    }
  },
  created() {
    this.loadDailySummary()
    this.loadMonthlySummary()
    this.calculateTableHeight()
    
    // 监听窗口大小变化，调整表格高度
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeUnmount() {
    // 移除事件监听
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    calculateTableHeight() {
      // 根据窗口高度动态计算表格高度
      const windowHeight = window.innerHeight
      this.tableHeight = `${windowHeight - 400}px`
    },
    
    async loadDailySummary() {
      this.dailyLoading = true
      try {
        const response = await statisticsAPI.getDailyPurchaseSummary(this.dailyForm.date)
        this.dailyData = response
      } catch (error) {
        console.error('加载每日汇总失败:', error)
        ElMessage.error('加载每日汇总失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.dailyLoading = false
      }
    },
    
    async loadMonthlySummary() {
      this.monthlyLoading = true
      try {
        const response = await statisticsAPI.getMonthlyCustomerSummary(
          this.monthlyForm.year, 
          this.monthlyForm.month
        )
        this.monthlyData = response
      } catch (error) {
        console.error('加载月度汇总失败:', error)
        ElMessage.error('加载月度汇总失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.monthlyLoading = false
      }
    },
    
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f7fa;
}

.container01_h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #303133;
}

.header-actions {
  margin-bottom: 20px;
}

.back-button {
  background-color: #409EFF;
  color: white;
  border: none;
}

.back-button:hover {
  background-color: #66b1ff;
  color: white;
}

.stats-tabs {
  margin-top: 20px;
}

.stats-card {
  margin-bottom: 20px;
}

.filter-form {
  margin-bottom: 20px;
}

/* 表格容器样式 - 修复 ResizeObserver 错误 */
.table-container {
  position: relative;
  min-height: 300px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

/* 嵌套表格容器 */
.nested-table-container {
  padding: 10px;
  background-color: #fafafa;
}

/* 金额高亮样式 */
.amount-highlight {
  font-weight: bold;
  color: #f56c6c;
}

.summary-info {
  margin-top: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  color: #606266;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 10px;
  }
  
  .table-container {
    overflow-x: auto;
  }
}

/* 确保表格不会产生布局震荡 */
:deep(.el-table) {
  flex: 1;
}

:deep(.el-table__body-wrapper) {
  scrollbar-width: thin;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb {
  background-color: #c1c1c1;
  border-radius: 3px;
}
</style>